import { useEffect, useState } from "react";
import { updateUserAccess, type User, type UserPermission, type UserRole } from "@/lib/api";
import { DialogFrame } from "@/components/common/DialogFrame";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { SelectInputField } from "@/components/common/SelectInputField";
import { ALL_USER_PERMISSIONS } from "@/lib/permissions";
import { PermissionCheckboxes } from "./PermissionCheckboxes";

interface EditUserAccessDialogProps {
	user: User | null;
	onClose: () => void;
	onSaved: () => void;
}

/** ユーザーのロールと、メンバーの場合は個別の権限を編集する。管理者は全権限を持つため権限欄を出さない。 */
export function EditUserAccessDialog({ user, onClose, onSaved }: EditUserAccessDialogProps) {
	const [role, setRole] = useState<UserRole>("Member");
	const [permissions, setPermissions] = useState<UserPermission[]>([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!user) return;
		setRole(user.role);
		setPermissions(user.permissions);
		setSaving(false);
		setError(null);
	}, [user]);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!user) return;
		setSaving(true);
		setError(null);
		try {
			await updateUserAccess(user.id, role, role === "Member" ? permissions : []);
			onSaved();
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : "権限の保存に失敗しました。");
			throw err;
		} finally {
			setSaving(false);
		}
	}

	return (
		<DialogFrame
			open={user !== null}
			title={user ? `${user.lastName} ${user.firstName} のロールと権限` : ""}
			width={480}
			onClose={() => {
				if (!saving) onClose();
			}}
			onSubmit={(e) => {
				handleSubmit(e).catch((err: unknown) => console.error(err));
			}}
			footer={
				<>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={saving}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						label="保存"
						loading={saving}
						data-testid="user-access-save"
					/>
				</>
			}
		>
			{error && (
				<div
					style={{
						padding: "10px 14px",
						borderRadius: "var(--radius-3)",
						background: "var(--danger-subtle)",
						boxShadow: "0 0 0 1px var(--danger-line)",
						fontSize: "var(--text-ui)",
						color: "var(--danger)",
					}}
				>
					{error}
				</div>
			)}
			<SelectInputField
				label="ロール"
				value={role}
				onChange={(v) => setRole(v as UserRole)}
				testId="user-access-role"
			>
				<option value="Member">メンバー</option>
				<option value="Admin">管理者</option>
			</SelectInputField>
			{/* 管理者は全権限を暗黙に持つため、すべて付いた状態で変更できなくする (送る権限は空のまま)。 */}
			<PermissionCheckboxes
				value={role === "Member" ? permissions : ALL_USER_PERMISSIONS}
				disabled={saving || role !== "Member"}
				onChange={setPermissions}
			/>
			{role !== "Member" && (
				<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					管理者はすべての権限を持ち、組織内のすべてのプロジェクトとユーザーを管理できます。
				</p>
			)}
		</DialogFrame>
	);
}
